import { routeParams } from '@/routes';
import { defaults } from 'lodash';
import { onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

type UseTabsOptions = {
  tabParam?: string;
};

export function useTabs<T extends object>(
  view: keyof typeof routeParams,
  tabs: T,
  initialTab?: T[keyof T],
  options: UseTabsOptions = {},
) {
  const { tabParam } = defaults(options, {
    tabParam: 'tab',
  });

  const route = useRoute();
  const activeTab = ref<keyof T>(initialTab ?? Object.values(tabs)[0]);

  function setActiveTab() {
    const routeParamsForView = routeParams[view] as Record<string, any>;
    if (
      tabParam in routeParams[view] &&
      typeof routeParamsForView[tabParam] === 'string'
    ) {
      const activeTabValue = route.params[routeParamsForView[tabParam]];
      if (activeTabValue) {
        activeTab.value = activeTabValue;
      }
    } else if (
      'query' in routeParams[view] &&
      routeParams[view].query &&
      typeof routeParams[view].query === 'object' &&
      tabParam in routeParams[view].query &&
      typeof routeParamsForView.query[tabParam] === 'string'
    ) {
      const activeTabValue = route.query[routeParamsForView.query[tabParam]];
      if (activeTabValue) {
        activeTab.value = activeTabValue;
      }
    } else {
      throw new Error('Missing or invalid tab parameter for view: ' + view);
    }
  }

  onMounted(setActiveTab);
  watch(() => route, setActiveTab, { deep: true, immediate: true });

  return {
    activeTab,
  };
}
